import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { useFile } from "../../context/FileContext";
import { useMonitoringText } from "../i18n";
import { downloadCsvReplayTemplate, importCsvReplay, validateCsvReplay } from "../services/monitoringApi";

const TIMEZONES = ["Europe/Athens", "Europe/Sofia", "Europe/Bucharest", "Europe/Berlin", "Europe/Paris", "Europe/Amsterdam", "Europe/Budapest", "UTC"];
const PLANT_FIELDS = new Set([
  "dc_kwp", "ac_kw", "lat", "lon", "timezone", "tilt_deg", "azimuth_deg", "mounting", "pr_target", "system_loss_pct",
]);

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

function projectDefaults(currentProject) {
  const config = currentProject?.config || {};
  const activeId = config.portfolio?.activeAssetId || "";
  const active = (config.portfolio?.assets || []).find((asset) => asset.id === activeId);
  const c = active?.config || config;
  return {
    name: `${active?.name || currentProject?.name || "PV park"} — CSV replay`,
    asset_id: activeId,
    plant: {
      dc_kwp: String(firstValue(c.pvInstalledCap?.kw, c.pvInstalledCapacityKw, "")),
      ac_kw: String(firstValue(c.gridConnection?.exportLimitKw, c.gridExportLimitKw, "")),
      lat: String(firstValue(c.loc_info?.lat, c.latitude, "")),
      lon: String(firstValue(c.loc_info?.lon, c.longitude, "")),
      timezone: firstValue(c.timeZone, c.timezone, "Europe/Athens"),
      tilt_deg: String(firstValue(c.panelOrientation?.tilt, 25)),
      azimuth_deg: String(firstValue(c.panelOrientation?.azim, 180)),
      mounting: ["tracking", "tracker", "tracker_1axis"].includes(c.mountType) ? "tracker_1axis" : "fixed",
      pr_target: "0.95",
      system_loss_pct: "14",
    },
  };
}

function sourceDefaults(source, fallback) {
  if (!source) return fallback;
  return {
    name: source.name || fallback.name,
    asset_id: source.asset_id || fallback.asset_id,
    plant: {
      ...fallback.plant,
      ...Object.fromEntries(Object.entries(source.plant || {})
        .filter(([key, value]) => PLANT_FIELDS.has(key) && value !== null && value !== undefined)
        .map(([key, value]) => [key, String(value)])),
    },
  };
}

export default function CsvReplayDialog({ open, source, projectId, accessToken, onClose, onImported }) {
  const t = useMonitoringText();
  const { currentProject } = useFile();
  const defaults = useMemo(() => sourceDefaults(source, projectDefaults(currentProject)), [source, currentProject]);
  const [form, setForm] = useState(defaults);
  const [mode, setMode] = useState("replay_to_now");
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm(defaults);
    setMode("replay_to_now");
    setFile(null);
    setSummary(null);
    setError(null);
    setBusy(null);
  }, [open, defaults]);

  const setPlant = (key, value) => {
    setForm((current) => ({ ...current, plant: { ...current.plant, [key]: value } }));
    if (key === "timezone") setSummary(null);
  };
  const selectFile = (selected) => { setFile(selected || null); setSummary(null); setError(null); };
  const validationData = () => {
    const data = new FormData();
    data.append("file", file);
    data.append("timezone_name", form.plant.timezone);
    data.append("mode", mode);
    return data;
  };
  const validate = async () => {
    setBusy("validate"); setError(null);
    try {
      const result = await validateCsvReplay(projectId, validationData(), { accessToken });
      setSummary(result.summary);
    } catch (e) { setError(e.message); } finally { setBusy(null); }
  };
  const importFile = async () => {
    setBusy("import"); setError(null);
    try {
      const data = validationData();
      data.append("name", form.name.trim());
      if (form.asset_id) data.append("asset_id", form.asset_id);
      if (source?.id) data.append("source_id", source.id);
      const plant = Object.fromEntries(Object.entries(form.plant).filter(([, value]) => value !== "" && value !== null && value !== undefined));
      data.append("plant_json", JSON.stringify(plant));
      const result = await importCsvReplay(projectId, data, { accessToken });
      onImported(result);
      onClose();
    } catch (e) { setError(e.message); } finally { setBusy(null); }
  };
  const getTemplate = async () => {
    setBusy("template"); setError(null);
    try {
      const blob = await downloadCsvReplayTemplate({ accessToken });
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = "PYTHIA_monitoring_csv_template.csv";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(href);
    } catch (e) { setError(e.message); } finally { setBusy(null); }
  };

  const missing = !file || !form.name.trim() || !form.plant.dc_kwp || !form.plant.lat || !form.plant.lon;

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="md">
      <DialogTitle>{source ? t("csv.title.append") : t("csv.title")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">{t("csv.intro")}</Alert>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <Button variant="outlined" onClick={getTemplate} disabled={Boolean(busy)}>{t("csv.template")}</Button>
            <Button component="label" variant="contained" disabled={Boolean(busy)}>
              {t("csv.choose")}
              <input hidden type="file" accept=".csv,text/csv" onChange={(event) => selectFile(event.target.files?.[0])} />
            </Button>
            <Typography variant="body2" color={file ? "text.primary" : "text.secondary"}>{file?.name || t("csv.noFile")}</Typography>
          </Stack>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth required size="small" label={t("form.name")} value={form.name} onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))} /></Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth select size="small" label={t("csv.mode")} value={mode} onChange={(e) => { setMode(e.target.value); setSummary(null); }}>
                <MenuItem value="replay_to_now">{t("csv.mode.replay")}</MenuItem>
                <MenuItem value="historical">{t("csv.mode.historical")}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth select size="small" label={t("form.tz")} value={form.plant.timezone} onChange={(e) => setPlant("timezone", e.target.value)}>
                {TIMEZONES.map((zone) => <MenuItem key={zone} value={zone}>{zone}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth required size="small" type="number" label={t("form.dc")} value={form.plant.dc_kwp} onChange={(e) => setPlant("dc_kwp", e.target.value)} /></Grid>
            <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" type="number" label={t("form.ac")} value={form.plant.ac_kw} onChange={(e) => setPlant("ac_kw", e.target.value)} /></Grid>
            <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth required size="small" type="number" label={t("form.lat")} value={form.plant.lat} onChange={(e) => setPlant("lat", e.target.value)} /></Grid>
            <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth required size="small" type="number" label={t("form.lon")} value={form.plant.lon} onChange={(e) => setPlant("lon", e.target.value)} /></Grid>
            <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" type="number" label={t("form.tilt")} value={form.plant.tilt_deg} onChange={(e) => setPlant("tilt_deg", e.target.value)} /></Grid>
            <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" type="number" label={t("form.azimuth")} value={form.plant.azimuth_deg} onChange={(e) => setPlant("azimuth_deg", e.target.value)} /></Grid>
            <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" type="number" label={t("form.prTarget")} value={form.plant.pr_target} onChange={(e) => setPlant("pr_target", e.target.value)} /></Grid>
            <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" type="number" label={t("form.loss")} value={form.plant.system_loss_pct} onChange={(e) => setPlant("system_loss_pct", e.target.value)} /></Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary">{mode === "replay_to_now" ? t("csv.mode.replayHint") : t("csv.mode.historicalHint")}</Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {summary && (
            <Alert severity="success">
              <Typography variant="subtitle2">{t("csv.valid")}</Typography>
              <Typography variant="body2">{t("csv.summary", summary)}</Typography>
              <Typography variant="body2">{summary.effective_start} → {summary.effective_end}</Typography>
              {(summary.warnings || []).map((warning) => <Typography key={warning} variant="caption" sx={{ display: "block" }}>• {warning}</Typography>)}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={Boolean(busy)}>{t("form.cancel")}</Button>
        <Button onClick={validate} disabled={missing || Boolean(busy)} startIcon={busy === "validate" ? <CircularProgress size={16} /> : null}>{t("csv.validate")}</Button>
        <Button variant="contained" onClick={importFile} disabled={missing || !summary || Boolean(busy)} startIcon={busy === "import" ? <CircularProgress size={16} color="inherit" /> : null}>{t("csv.import")}</Button>
      </DialogActions>
    </Dialog>
  );
}
